import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SimpleGrid, Box, HStack, VStack, Text, Icon, Spinner, Flex, Button, Center,
  Input, Badge, Table, Thead, Tbody, Tr, Th, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import {
  MdRoute, MdLocalShipping, MdWarning, MdHandyman, MdBuild, MdLightbulb, MdConstruction,
  MdAssignment,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import {
  rutasApi, camionesApi, faenasApi, alumbradoApi, herramientasApi, bitacoraApi,
  Ruta, Camion, Faena, ReporteAlumbrado, Herramienta, Bitacora,
} from '../../../api/serviciosApi';

const RED = '#5b182b';
const RED_DARK = '#691f30';

const ESTATUS_COLORS: Record<string, string> = {
  activo: 'green',
  inactivo: 'gray',
  Disponible: 'green',
  'Fuera de servicio': 'red',
  Pendiente: 'yellow',
  Programada: 'purple',
  'En proceso': 'blue',
  Finalizado: 'green',
  Finalizada: 'green',
  Atendido: 'green',
  Cancelado: 'red',
  Cancelada: 'red',
};

const fmt = (v?: string) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const EstatusBadge = ({ v }: { v?: string }) => (
  <Badge colorScheme={ESTATUS_COLORS[v || ''] || 'gray'} fontSize="2xs" px={2} py={0.5} borderRadius="full">
    {v || '—'}
  </Badge>
);

// ─────────────────────────── Tipos ───────────────────────────

interface Column<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
}

interface Tarjeta {
  key: string;
  label: string;
  value: number;
  icon: IconType;
  color: string;
  rows: any[];
  columns: Column<any>[];
  emptyText: string;
}

// ─────────────────────────── Tarjeta KPI ───────────────────────────

const StatCard: React.FC<{ t: Tarjeta; onClick: () => void }> = ({ t, onClick }) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    borderLeft={`4px solid ${t.color}`}
    p={5}
    cursor="pointer"
    role="button"
    onClick={onClick}
    _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
    _active={{ transform: 'translateY(-1px)' }}
    transition="all 0.15s"
  >
    <HStack spacing={4} align="center">
      <Center bg={`${t.color}22`} borderRadius="full" boxSize="52px" flexShrink={0}>
        <Icon as={t.icon} boxSize={6} color={t.color} />
      </Center>
      <VStack align="start" spacing={0}>
        <Text fontSize="3xl" fontWeight="bold" color="gray.700" lineHeight="1">{t.value}</Text>
        <Text fontSize="sm" color="gray.500">{t.label}</Text>
      </VStack>
    </HStack>
  </Box>
);

// ─────────────────────────── Columnas por KPI ───────────────────────────

const colsRutas: Column<Ruta>[] = [
  { header: 'Código', cell: (r) => r.codigo || '—' },
  { header: 'Nombre', cell: (r) => r.nombre || '—' },
  { header: 'Localidad', cell: (r) => r.localidad || '—' },
  { header: 'Colonia', cell: (r) => r.colonia || '—' },
  { header: 'Días de servicio', cell: (r) => r.diasServicio || '—' },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

const colsCamiones: Column<Camion>[] = [
  { header: 'No. Económico', cell: (c) => c.numeroEconomico || '—' },
  { header: 'Placas', cell: (c) => c.placas || '—' },
  { header: 'Marca', cell: (c) => c.marca || '—' },
  { header: 'Modelo', cell: (c) => c.modelo || '—' },
  { header: 'Capacidad', cell: (c) => c.capacidad || '—' },
  { header: 'Estado', cell: (c) => <EstatusBadge v={c.estadoCamion} /> },
];

const colsFaenas: Column<Faena>[] = [
  { header: 'Folio', cell: (f) => f.folio || '—' },
  { header: 'Fecha', cell: (f) => fmt(f.fecha) },
  { header: 'Tipo', cell: (f) => f.tipoFaena?.descripcion || '—' },
  { header: 'Prioridad', cell: (f) => f.prioridad || '—' },
  { header: 'Responsable', cell: (f) => f.responsable || '—' },
  { header: 'Localidad', cell: (f) => f.localidad || '—' },
  { header: 'Estatus', cell: (f) => <EstatusBadge v={f.estatus} /> },
];

const colsAlumbrado: Column<ReporteAlumbrado>[] = [
  { header: 'Folio', cell: (a) => a.folio || '—' },
  { header: 'Fecha', cell: (a) => fmt(a.fecha) },
  { header: 'Origen', cell: (a) => a.origenReporte || '—' },
  { header: 'Localidad', cell: (a) => a.localidad || '—' },
  { header: 'Colonia', cell: (a) => a.colonia || '—' },
  { header: 'Responsable', cell: (a) => a.responsable || '—' },
  { header: 'Estatus', cell: (a) => <EstatusBadge v={a.estatus} /> },
];

const colsHerramientas: Column<Herramienta>[] = [
  { header: 'SKU', cell: (h) => h.sku || '—' },
  { header: 'Nombre', cell: (h) => h.nombre || '—' },
  { header: 'Categoría', cell: (h) => h.categoria?.nombre || '—' },
  { header: 'Total', cell: (h) => h.cantidad ?? 0 },
  { header: 'Disponible', cell: (h) => h.cantidadDisponible ?? 0 },
  { header: 'Prestadas', cell: (h) => Math.max(0, (h.cantidad || 0) - (h.cantidadDisponible || 0)) },
];

const colsBitacora: Column<Bitacora>[] = [
  { header: 'Fecha', cell: (b) => fmt(b.fecha) },
  { header: 'Área', cell: (b) => b.area || '—' },
  { header: 'Tipo', cell: (b) => b.tipoActividad || '—' },
  { header: 'Actividad', cell: (b) => <Text noOfLines={2} maxW="260px">{b.actividad || '—'}</Text> },
  { header: 'Responsable', cell: (b) => b.responsable || '—' },
  { header: 'Estatus', cell: (b) => <EstatusBadge v={b.estatus} /> },
];

// ─────────────────────────── Utilidades de fecha ───────────────────────────

const toISO = (d: Date) => d.toISOString().slice(0, 10);
const inicioMes = () => { const d = new Date(); return toISO(new Date(d.getFullYear(), d.getMonth(), 1)); };
const finMes = () => { const d = new Date(); return toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

// ─────────────────────────── Componente principal ───────────────────────────

const TarjetasResumen: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [tarjetas, setTarjetas] = useState<Tarjeta[]>([]);

  // Rango aplicado (afecta a los KPIs) y rango pendiente (inputs)
  const [desde, setDesde] = useState<string>(inicioMes());
  const [hasta, setHasta] = useState<string>(finMes());
  const [pendDesde, setPendDesde] = useState<string>(inicioMes());
  const [pendHasta, setPendHasta] = useState<string>(finMes());

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [seleccion, setSeleccion] = useState<Tarjeta | null>(null);

  const cargar = useCallback(async (fInicio: string, fFin: string) => {
    setCargando(true);
    try {
      const rango: any = {};
      if (fInicio) rango.fechaInicio = fInicio;
      if (fFin) rango.fechaFin = fFin;

      const [rutas, camiones, faenas, alumbrado, herramientas, bitacora] = await Promise.all([
        rutasApi.list().catch(() => [] as Ruta[]),
        camionesApi.list().catch(() => [] as Camion[]),
        faenasApi.list(rango).catch(() => [] as Faena[]),
        alumbradoApi.list(rango).catch(() => [] as ReporteAlumbrado[]),
        herramientasApi.list().catch(() => [] as Herramienta[]),
        bitacoraApi.list(rango).catch(() => [] as Bitacora[]),
      ]);

      const rutasActivas = (rutas || []).filter((r) => r.estatus === 'activo');
      const camionesDisponibles = (camiones || []).filter((c) => c.estadoCamion === 'Disponible');
      const camionesFuera = (camiones || []).filter((c) => c.estadoCamion === 'Fuera de servicio');
      const faenasPendientes = (faenas || []).filter((f) => f.estatus === 'Pendiente');
      const faenasEnProceso = (faenas || []).filter((f) => f.estatus === 'En proceso');
      const alumbradoPendientes = (alumbrado || []).filter((a) => a.estatus === 'Pendiente');
      const herramientasPrestadas = (herramientas || []).filter(
        (h) => Math.max(0, (h.cantidad || 0) - (h.cantidadDisponible || 0)) > 0,
      );
      const totalPrestadas = herramientasPrestadas.reduce(
        (acc, h) => acc + Math.max(0, (h.cantidad || 0) - (h.cantidadDisponible || 0)),
        0,
      );

      setTarjetas([
        {
          key: 'rutas', label: 'Rutas activas', value: rutasActivas.length, icon: MdRoute, color: '#2F855A',
          rows: rutasActivas, columns: colsRutas, emptyText: 'No hay rutas activas.',
        },
        {
          key: 'camDisp', label: 'Camiones disponibles', value: camionesDisponibles.length, icon: MdLocalShipping, color: '#2B6CB0',
          rows: camionesDisponibles, columns: colsCamiones, emptyText: 'No hay camiones disponibles.',
        },
        {
          key: 'camFuera', label: 'Camiones fuera de servicio', value: camionesFuera.length, icon: MdWarning, color: '#C53030',
          rows: camionesFuera, columns: colsCamiones, emptyText: 'No hay camiones fuera de servicio.',
        },
        {
          key: 'faenasPend', label: 'Faenas pendientes', value: faenasPendientes.length, icon: MdConstruction, color: '#B7791F',
          rows: faenasPendientes, columns: colsFaenas, emptyText: 'No hay faenas pendientes en el rango.',
        },
        {
          key: 'faenasProc', label: 'Faenas en proceso', value: faenasEnProceso.length, icon: MdBuild, color: '#805AD5',
          rows: faenasEnProceso, columns: colsFaenas, emptyText: 'No hay faenas en proceso en el rango.',
        },
        {
          key: 'alumbrado', label: 'Reportes de alumbrado pendientes', value: alumbradoPendientes.length, icon: MdLightbulb, color: '#D69E2E',
          rows: alumbradoPendientes, columns: colsAlumbrado, emptyText: 'No hay reportes de alumbrado pendientes en el rango.',
        },
        {
          key: 'herramientas', label: 'Herramientas prestadas', value: totalPrestadas, icon: MdHandyman, color: RED,
          rows: herramientasPrestadas, columns: colsHerramientas, emptyText: 'No hay herramientas prestadas.',
        },
        {
          key: 'bitacora', label: 'Actividades del periodo', value: (bitacora || []).length, icon: MdAssignment, color: RED_DARK,
          rows: bitacora || [], columns: colsBitacora, emptyText: 'No hay actividades registradas en el rango.',
        },
      ]);
    } catch {
      setTarjetas([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(desde, hasta); /* eslint-disable-next-line */ }, []);

  const aplicar = () => {
    setDesde(pendDesde);
    setHasta(pendHasta);
    cargar(pendDesde, pendHasta);
  };

  const limpiar = () => {
    setPendDesde('');
    setPendHasta('');
    setDesde('');
    setHasta('');
    cargar('', '');
  };

  const abrirDetalle = (t: Tarjeta) => { setSeleccion(t); onOpen(); };

  const rangoTexto = useMemo(() => {
    if (!desde && !hasta) return 'Todos los registros';
    return `${desde ? fmt(desde) : 'inicio'} — ${hasta ? fmt(hasta) : 'hoy'}`;
  }, [desde, hasta]);

  return (
    <Box>
      {/* ─── Filtro de fecha ─── */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" borderTop={`4px solid ${RED}`} p={4} mb={5}>
        <Flex justify="space-between" align="flex-end" flexWrap="wrap" gap={3}>
          <HStack spacing={4} flexWrap="wrap" align="flex-end">
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Desde</Text>
              <Input
                type="date" size="sm" w="160px" value={pendDesde}
                onChange={(e) => setPendDesde(e.target.value)}
                focusBorderColor={RED}
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Hasta</Text>
              <Input
                type="date" size="sm" w="160px" value={pendHasta}
                onChange={(e) => setPendHasta(e.target.value)}
                focusBorderColor={RED}
              />
            </Box>
            <Button
              size="sm" color="white" bg={RED} _hover={{ bg: RED_DARK }}
              onClick={aplicar} isLoading={cargando}
            >
              Aplicar
            </Button>
            <Button size="sm" variant="outline" colorScheme="red" onClick={limpiar} isDisabled={cargando}>
              Limpiar
            </Button>
          </HStack>
          <HStack spacing={3}>
            <Text fontSize="xs" color="gray.500">Periodo: <b>{rangoTexto}</b></Text>
            <Button
              size="sm" leftIcon={<RepeatIcon />} variant="ghost" colorScheme="red"
              onClick={() => cargar(desde, hasta)} isLoading={cargando}
            >
              Actualizar
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* ─── KPIs ─── */}
      {cargando ? (
        <Center py={16}><Spinner size="xl" color={RED} thickness="3px" /></Center>
      ) : tarjetas.length === 0 ? (
        <Center py={16}><Text color="gray.400">No se pudieron cargar los indicadores.</Text></Center>
      ) : (
        <>
          <Text fontSize="xs" color="gray.400" mb={2}>
            Da clic en cualquier indicador para ver el detalle de los registros.
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
            {tarjetas.map((t) => <StatCard key={t.key} t={t} onClick={() => abrirDetalle(t)} />)}
          </SimpleGrid>
        </>
      )}

      {/* ─── Modal drill-down ─── */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={RED} borderBottom="1px solid" borderColor="gray.200">
            {seleccion?.label}
            {seleccion && (
              <Text as="span" fontSize="sm" color="gray.500" fontWeight="normal" ml={2}>
                ({seleccion.value}) · {rangoTexto}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            {seleccion && (
              seleccion.rows.length === 0 ? (
                <Center py={10}><Text color="gray.400">{seleccion.emptyText}</Text></Center>
              ) : (
                <Box overflowX="auto" borderRadius="md" boxShadow="sm">
                  <Table variant="simple" size="sm">
                    <Thead bg={RED}>
                      <Tr>
                        {seleccion.columns.map((c) => (
                          <Th key={c.header} color="white" fontSize="xs">{c.header}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {seleccion.rows.map((row, idx) => (
                        <Tr key={row.id ?? idx} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                          {seleccion.columns.map((c) => (
                            <Td key={c.header} fontSize="sm">{c.cell(row)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TarjetasResumen;
